import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import * as _ from 'lodash';
import { ChatRenderService } from '../services/gfchat.service';
import { UiService } from '../../../core/services/core.ui.service';
import { doDeletePromise } from '../../../core/config/core.httpclient-utils';
import { HttpClient } from '@angular/common/http';
import { ChatStatusService } from '../../../core/services/core.chat-status.service';

@Component({
    selector: 'message-list',
    template: require('./chat.message-list.component.html'),
})

export class MessageListComponent implements OnInit, OnChanges, OnDestroy {
    /** \gforge\core\models\User::USER_ID_NOBODY */
    private static readonly USER_ID_NOBODY = 100;

    @Input() messages: any;
    @Input() isAdmin: boolean = false;
    /** Project unix name/id, when known, used to resolve `git:<hash>` refs. */
    @Input() project: string = '';
    /**
     * Live chat status of the users in the room, by user id; null until the
     * room's user list has arrived. A message's own `user.chatStatus` is only
     * what it was when the message was fetched, and is missing altogether on
     * older messages and activity/wall entries.
     */
    @Input() userStatuses: { [userId: string]: string } | null = null;

    constructor(
        private chatRenderService: ChatRenderService,
        private uiService: UiService,
        private http: HttpClient,
        private chatStatusService: ChatStatusService
    ){ }

    public filterUser;
    public goToActivityItem;

    /** Id of the message whose copy icon is currently showing its "done" state. */
    public copiedMessageId: any = null;

    private copiedResetTimer: any = null;

    ngOnInit() {
        this.filterUser = false;
        this.goToActivityItem = _.partialRight(this.uiService.state.goToActivity, { newWindow: true, target: '_blank' });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.messages) {
            this.messages = _.map(
                this.messages,
                (msg, i:any) => {
                    msg.dt = this.chatRenderService.delimiterText(i, this.messages);
                    msg.isCurrentUser = this.chatRenderService.isCurrentUser(msg.user.unixName);
                    msg.hasMention = this.chatRenderService.hasMention(msg);
                    if (msg.type === 'activity') {
                        msg.activityUrl = this.uiService.state.getActivityUrl(msg.activity) || null;
                    }
                    return msg;
                }
            )
        }
    }

    /**
     * Activity links with a static URL are real hrefs, so the browser opens
     * them (and they can be copied or middle-clicked). Sections without one,
     * like wiki pages, still resolve the item on click.
     */
    openActivityItem(event: Event, message: any) {
        if (message.activityUrl) {
            return;
        }
        event.preventDefault();
        this.goToActivityItem(message.activity);
    }

    /**
     * Open a wall post's item in a new window, like activity links. The standalone
     * chat app only registers the chat states, so an in-app state change to the
     * item fails silently there.
     */
    goToWallItem(wallpost: any) {
        if (wallpost.itemSection && wallpost.itemRefId) {
            this.uiService.state.goToItem(wallpost.itemSection, wallpost.itemRefId, { newWindow: true, target: '_blank' }, wallpost?.project?.unixName);
        }
    }

    /**
     * Copy a message's stored body to the clipboard.
     *
     * Note this means a legacy (pre-Markdown) message copies as the HTML it was
     * stored as -- those bodies have no Markdown source to recover.
     */
    copyMessage(message) {
        const body = (message && message.body) ? String(message.body) : '';
        if (!body) return;

        this.writeToClipboard(body).then((ok) => {
            if (!ok) return;

            this.copiedMessageId = message.id;
            if (this.copiedResetTimer) clearTimeout(this.copiedResetTimer);
            this.copiedResetTimer = setTimeout(() => {
                this.copiedMessageId = null;
                this.copiedResetTimer = null;
            }, 1500);
        });
    }

    private writeToClipboard(text: string): Promise<boolean> {
        const clip = (navigator as any).clipboard;
        if (clip && typeof clip.writeText === 'function') {
            return clip.writeText(text)
                .then(() => true)
                .catch(() => this.execCommandCopy(text));
        }
        return Promise.resolve(this.execCommandCopy(text));
    }

    private execCommandCopy(text: string): boolean {
        try {
            const scratch = document.createElement('textarea');
            scratch.value = text;
            // Off-screen rather than display:none -- a hidden element cannot be
            // selected, and selecting is what execCommand('copy') acts on.
            scratch.style.position = 'fixed';
            scratch.style.left = '-9999px';
            scratch.setAttribute('readonly', '');
            document.body.appendChild(scratch);
            scratch.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(scratch);
            return ok;
        } catch (e) {
            console.warn('chat: copy to clipboard failed', e);
            return false;
        }
    }

    ngOnDestroy(): void {
        if (this.copiedResetTimer) {
            clearTimeout(this.copiedResetTimer);
            this.copiedResetTimer = null;
        }
    }

    /**
     * The status to show by a message's avatar: the current user's own as
     * soon as they change it; otherwise the room's live list, where anyone
     * not in it is shown offline. Until that list arrives, the status the
     * message came with, if any.
     */
    statusFor(message): string | null {
        if (message.isCurrentUser) {
            return this.chatStatusService.status;
        }
        const userId = message?.user?.id;
        // No dot for the system user activity entries are posted as
        if (!userId || userId == MessageListComponent.USER_ID_NOBODY) {
            return null;
        }
        if (this.userStatuses) {
            return this.userStatuses[userId] || 'offline';
        }
        return message?.user?.chatStatus || null;
    }

    setFilterUser(user) {
        if (_.get(this, 'filterUser.id', 0) == user.id) {
            this.filterUser = false;
        } else {
            this.filterUser = user;
        }
    };

    sendDeleteMessage(messageId) {
        // No need to wait for response,
        // the deletion comes through
        // on the next poll.
        // ctrl.ForumMessageSvc.delete({ 'id': messageId }).$promise
        //     .then(
        //         function () {
        //             ctrl.messages = ChatRenderService.deleteMessages(ctrl.messages, [messageId]);
        //         }, function () { }
        //     );
        const url = '/api/forummessage';
        doDeletePromise(this.http, url, { id: messageId })
        .then(
            () => {
                this.messages = this.chatRenderService.deleteMessages(this.messages, [messageId]);
            }, (response) => { console.log(response); }
        );
    };

    trackby(index: number, message: any): any {
        return message.trackby;
    }
}